import gsap from "gsap";
import { ExternalLink } from "lucide-react";
import React, { useEffect, useRef } from "react";

const ProjectCard = ({ key, project }) => {
  const hoverRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    const hover = hoverRef.current;

    let xTo = gsap.quickTo(hover, "x", {
      duration: 0.35,
      ease: "power3.out",
    });

    let yTo = gsap.quickTo(hover, "y", {
      duration: 0.35,
      ease: "power3.out",
    });

    const enter = () => {
      gsap.to(hover, {
        opacity: 1,
        scale: 1,
        duration: 0.35,
        ease: "back.out(2)",
      });
    };

    const leave = () => {
      gsap.to(hover, {
        opacity: 0,
        scale: 0.75,
        duration: 0.25,
        ease: "power2.out",
      });
    };

    const move = (e) => {
      const rect = card.getBoundingClientRect();

      const x = e.clientX - rect.left + 24;
      const y = e.clientY - rect.top - 20;

      xTo(x);
      yTo(y);
    };

    card.addEventListener("mouseenter", enter);
    card.addEventListener("mouseleave", leave);
    card.addEventListener("mousemove", move);

    return () => {
      card.removeEventListener("mouseenter", enter);
      card.removeEventListener("mouseleave", leave);
      card.removeEventListener("mousemove", move);
    };
  }, []);

  return (
    <>
      <div
        key={project?.id}
        className="project-card relative w-[320px] md:w-[400px] h-[550px] md:h-[600px] flex flex-col rounded-2xl"
        ref={cardRef}
      >
        <a
          href={project.live || "https://jatinporiya.epizy.com"}
          target="_blank"
          className="relative block overflow-visible w-full h-full overflow-hidden flex flex-col will-change-transform rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl"
        >
          {/* Card Image */}
          <div className="h-[220px] md:h-[260px] w-full relative overflow-hidden group rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] to-transparent z-10"></div>
            <img
              src={project?.image}
              alt={project?.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />

            {/* Floating Badges inside Image area */}
            <div className="absolute top-4 left-4 z-20 flex gap-2">
              <span className="px-3 py-1 text-xs font-semibold bg-black/50 backdrop-blur-md border border-white/10 rounded-full text-slate-200">
                {project?.type}
              </span>
              <span className="px-3 py-1 text-xs font-semibold bg-black/50 backdrop-blur-md border border-white/10 rounded-full text-slate-200">
                {project?.status}
              </span>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-6 md:p-8 flex flex-col flex-grow justify-between relative z-20 bg-gradient-to-b from-[#0f172a]/80 to-[#020617]">
            <div>
              <h3 className="text-2xl font-bold text-white mb-3">
                {project?.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                {project?.desc}
              </p>
            </div>

            <div>
              {/* Tech Stack Pills */}
              <div className="flex flex-wrap gap-2 mb-8">
                {project?.stack?.map((tech, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-medium text-slate-300 bg-white/5 border border-white/5 px-2.5 py-1 rounded-md"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4">
                <button
                  className="flex-1 bg-white text-black font-semibold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors"
                  style={{ zIndex: 99 }}
                >
                  <ExternalLink className="w-4 h-4" /> Live Demo
                </button>

                <button className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white transition-colors">
                  {/* <Github className="w-5 h-5" /> */}
                  GitHub
                </button>
              </div>
            </div>
          </div>
        </a>

        <div
          ref={hoverRef}
          className="pointer-events-none absolute z-50 px-5 py-3 rounded-2xl backdrop-blur-xl bg-white/[0.06] border border-white/10 shadow-[0_8px_50px_rgba(255,255,255,0.08)] text-white font-semibold flex items-center gap-2 opacity-0 scale-75"
        >
          <span>Explore Project</span>

          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M7 17L17 7" />
            <path d="M7 7h10v10" />
          </svg>
        </div>
      </div>
    </>
  );
};

export default ProjectCard;
